import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import {
  Box, Container, Typography, Button, Grid, Stack, Paper,
  Chip, Divider, Avatar
} from '@mui/material';
import ArrowForwardIcon    from '@mui/icons-material/ArrowForward';
import ArrowOutwardIcon    from '@mui/icons-material/ArrowOutward';
import AutorenewIcon       from '@mui/icons-material/Autorenew';
import ShowChartIcon       from '@mui/icons-material/ShowChart';
import SmartToyIcon        from '@mui/icons-material/SmartToy';
import BoltIcon            from '@mui/icons-material/Bolt';
import SavingsIcon         from '@mui/icons-material/Savings';

import Logo from '../../resources/logob.png';
import { PieChart, Pie, Cell } from 'recharts';
import { SiBitcoin, SiChainlink } from 'react-icons/si';

/* ─── Keyframe animations ────────────────────────────────── */
const pulseDot = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.7); }
  70%  { box-shadow: 0 0 0 9px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
`;

const floatA = keyframes`
  0%,100% { transform: translate(0px,   0px)   scale(1);    }
  33%     { transform: translate(18px, -14px)  scale(1.08); }
  66%     { transform: translate(-10px, 12px)  scale(0.94); }
`;

const floatB = keyframes`
  0%,100% { transform: translate(0px,  0px)   scale(1);    }
  33%     { transform: translate(-14px, 10px) scale(1.06); }
  66%     { transform: translate(12px, -18px) scale(0.92); }
`;

const floatC = keyframes`
  0%,100% { transform: translate(0px,  0px)   scale(1);    }
  50%     { transform: translate(-8px, -10px) scale(1.1);  }
`;

const scanLine = keyframes`
  0%   { top: -2px;   opacity: 0;   }
  4%   { opacity: 0.55; }
  96%  { opacity: 0.55; }
  100% { top: 100%;   opacity: 0;   }
`;

const shimmerText = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;
const rotateSlow = keyframes`
  from { transform: rotate(0deg);   }
  to   { transform: rotate(360deg); }
`;
const breatheGold = keyframes`
  0%,100% { box-shadow: 0 0 0 0 rgba(210,157,92,0);           }
  50%     { box-shadow: 0 0 28px 6px rgba(210,157,92,0.18);   }
`;
const breatheGreen = keyframes`
  0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,0);            }
  50%     { box-shadow: 0 0 28px 6px rgba(34,197,94,0.22);    }
`;
const flowRight = keyframes`
  0%   { left: -8px; opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { left: 48px; opacity: 0; }
`;
const flowLeft = keyframes`
  0%   { left: 48px; opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { left: -8px; opacity: 0; }
`;
const flowDown = keyframes`
  0%   { top: -8px; opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { top: 32px; opacity: 0; }
`;
const flowUp = keyframes`
  0%   { top: 32px; opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { top: -8px; opacity: 0; }
`;
const barGrow = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

/* ─── Animated flow-line arrow ───────────────────────────── */
const FlowLine = ({ label, dir = 'right', delay = '0s' }) => {
  const isV = dir === 'down' || dir === 'up';
  const anim = { right: flowRight, left: flowLeft, down: flowDown, up: flowUp }[dir];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', fontSize: '0.58rem', lineHeight: 1 }}>
        {label}
      </Typography>
      <Box sx={{ position: 'relative', width: isV ? 3 : 48, height: isV ? 28 : 3, bgcolor: 'rgba(210,157,92,0.18)', borderRadius: 99, overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', width: 8, height: 8, borderRadius: '50%',
          bgcolor: 'primary.main', boxShadow: '0 0 6px rgba(210,157,92,0.8)',
          ...(isV ? { left: '50%', ml: '-4px', top: '-8px' } : { top: '50%', mt: '-4px', left: '-8px' }),
          animation: `${anim} 2s ease-in-out infinite`,
          animationDelay: delay,
        }} />
      </Box>
    </Box>
  );
};

/* ─── Bots overview list ─────────────────────────────────── */
const BOTS_OVERVIEW = [
  { label: 'BTC DCA',                color: '#F7931A' },
  { label: 'BTC Max Exposure',       color: '#c8904a' },
  { label: 'ETH DCA',                color: '#8a92b2' },
  { label: 'ETH Max Exposure',       color: '#627EEA' },
  { label: 'LINK Max Exposure',      color: '#2A5ADA' },
  { label: 'LINK DCA',               color: '#1c3fa8' },
  { label: 'EBONDS & ESIR Buybacks', color: '#22c55e' },
];

/* ─── Live system card (static — lists the active bots) ──── */
const LiveSystemCard = () => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';

  /* ── Derived theme tokens ── */
  const cardBg      = isDark ? '#0d1520'                       : '#ffffff';
  const cardBorder  = isDark ? 'rgba(210,157,92,0.22)'         : 'rgba(200,144,74,0.25)';
  const cardShadow  = isDark
    ? '0 28px 70px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)'
    : '0 20px 60px rgba(10,16,32,0.12), 0 0 0 1px rgba(200,144,74,0.08)';
  const blobOpacity = isDark ? [0.11, 0.10, 0.13] : [0.07, 0.06, 0.09];
  const rowBg       = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(10,16,32,0.03)';
  const rowBd       = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,16,32,0.06)';

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 390, mx: 'auto' }}>
      <Box sx={{ bgcolor: cardBg, borderRadius: '20px', overflow: 'hidden', border: `1px solid ${cardBorder}`, boxShadow: cardShadow, position: 'relative', transition: 'background-color 0.3s, box-shadow 0.3s' }}>

        {/* ── Plasma blobs ── */}
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 'inherit', pointerEvents: 'none', zIndex: 0 }}>
          <Box sx={{ position: 'absolute', top: '5%',  left: '5%',  width: 150, height: 150, bgcolor: '#F7931A', filter: 'blur(72px)', opacity: blobOpacity[0], animation: `${floatA} 10s ease-in-out infinite` }} />
          <Box sx={{ position: 'absolute', bottom: '5%', right: '5%', width: 130, height: 130, bgcolor: '#2A5ADA', filter: 'blur(65px)', opacity: blobOpacity[1], animation: `${floatB} 13s ease-in-out infinite` }} />
          <Box sx={{ position: 'absolute', top: '45%', left: '35%', width: 110, height: 110, bgcolor: '#d29d5c', filter: 'blur(55px)', opacity: blobOpacity[2], animation: `${floatC} 8s ease-in-out infinite 1.5s` }} />
        </Box>

        {/* ── Scan line ── */}
        <Box sx={{ position: 'absolute', left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent 0%, rgba(210,157,92,0.55) 50%, transparent 100%)', animation: `${scanLine} 5s linear infinite`, zIndex: 1, pointerEvents: 'none' }} />

        {/* ── Content ── */}
        <Box sx={{ position: 'relative', zIndex: 2, p: 3 }}>

          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0, animation: `${pulseDot} 2.2s ease-out infinite` }} />
              <Box>
                <Typography variant="caption" color="text.primary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.7rem', display: 'block', lineHeight: 1.3 }}>
                  Live System
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                  Multiple active bots
                </Typography>
              </Box>
            </Stack>
            <Chip label={`${BOTS_OVERVIEW.length} BOTS ACTIVE`} size="small" sx={{ bgcolor: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)', fontWeight: 700, fontSize: '0.62rem', height: 20, borderRadius: '6px !important', flexShrink: 0 }} />
          </Stack>

          {/* Bots list — static, no simulated live figures */}
          <Stack spacing={0.6} sx={{ mb: 2.5 }}>
            {BOTS_OVERVIEW.map(b => (
              <Stack key={b.label} direction="row" alignItems="center" spacing={1.5}
                sx={{ py: 1, px: 1.5, bgcolor: rowBg, borderRadius: '10px', border: `1px solid ${rowBd}` }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: b.color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.primary" sx={{ fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif', fontSize: '0.78rem' }}>
                  {b.label}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* Stats row */}
          <Stack direction="row" spacing={1.5}>
            <Box sx={{ flex: 1, p: 1.5, bgcolor: 'rgba(34,197,94,0.08)', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.15)', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.25 }}>EBONDS Buyback Target</Typography>
              <Typography sx={{ color: '#22c55e', fontWeight: 800, fontFamily: '"Space Grotesk", sans-serif', fontSize: '1.15rem', lineHeight: 1 }}>$0.90</Typography>
            </Box>
            <Box sx={{ flex: 1, p: 1.5, bgcolor: 'rgba(210,157,92,0.08)', borderRadius: '10px', border: '1px solid rgba(210,157,92,0.15)', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.25 }}>Bots Layer Combined APY</Typography>
              <Typography sx={{ color: theme.palette.primary.main, fontWeight: 800, fontFamily: '"Space Grotesk", sans-serif', fontSize: '1.15rem', lineHeight: 1 }}>6–34%</Typography>
            </Box>
          </Stack>

        </Box>
      </Box>
    </Box>
  );
};

/* ─────────────────────────────────────────────────────────────
   HERO CARD
   ─────────────────────────────────────────────────────────────*/
const HeroCard = () => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 320, mx: 'auto' }}>
      {isDark && (
        <Box sx={{ position: 'absolute', top: '10%', left: '5%', width: '90%', height: '80%', background: '#d29d5c', filter: 'blur(100px)', opacity: 0.14, zIndex: 0 }} />
      )}
      <Paper sx={{
        p: 3, position: 'relative', zIndex: 1,
        borderRadius: '20px !important',
        border: isDark ? '1px solid rgba(210,157,92,0.2) !important' : 'none !important',
        boxShadow: isDark
          ? '0 24px 60px rgba(0,0,0,0.5) !important'
          : '0 20px 60px rgba(10,16,32,0.13) !important',
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 32, height: 32, bgcolor: isDark ? 'rgba(210,157,92,0.15)' : '#fdf3e7', border: `1px solid ${isDark ? 'rgba(210,157,92,0.3)' : '#f0d9b8'}` }}>
              <img src={Logo} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>eBonds App</Typography>
              <Typography variant="body2" color="text.primary" fontWeight={700} sx={{ fontFamily: '"Space Grotesk", sans-serif' }}>Live Strategies</Typography>
            </Box>
          </Stack>
          <Chip label="LIVE" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 700, fontSize: '0.65rem', border: '1px solid #a7f3d0', height: 22, borderRadius: '6px !important' }} />
        </Stack>

        <Typography variant="h3" color="text.primary" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, lineHeight: 1, mb: 0.25 }}>$0.90</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>EBONDS buyback target</Typography>
        <Divider sx={{ mb: 2.5 }} />

        <Stack spacing={1.5} sx={{ mb: 2.5 }}>
          {[
            { label: 'BTC DCA Bot',      apy: '20–30%',  color: '#F7931A', bg: isDark ? 'rgba(247,147,26,0.08)' : '#fff8f0' },
            { label: 'BTC Max Exposure', apy: '20–30%',  color: '#c8904a', bg: isDark ? 'rgba(200,144,74,0.08)' : '#fdf6ee' },
            { label: 'LINK DCA Bot',     apy: '60–200%', color: '#2A5ADA', bg: isDark ? 'rgba(42,90,218,0.08)'  : '#eff4ff' },
          ].map(b => (
            <Stack key={b.label} direction="row" justifyContent="space-between" alignItems="center"
              sx={{ p: 1.25, bgcolor: b.bg, borderRadius: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: b.color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{b.label}</Typography>
              </Stack>
              <Typography variant="caption" color="text.primary" sx={{ fontWeight: 800, fontFamily: '"Space Grotesk", sans-serif' }}>{b.apy} APY</Typography>
            </Stack>
          ))}
        </Stack>

        <Box sx={{ p: 1.5, bgcolor: isDark ? 'rgba(210,157,92,0.08)' : '#fdf6ee', borderRadius: 2, textAlign: 'center', border: `1px solid ${isDark ? 'rgba(210,157,92,0.2)' : '#f0d9b8'}` }}>
          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.04em' }}>
            COMBINED TARGET · 20–40% APY
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{
        position: 'absolute', bottom: -16, left: -16, zIndex: 2,
        px: 2, py: 1.25, borderRadius: '12px !important',
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0, boxShadow: '0 0 6px #22c55e' }} />
        <Typography variant="caption" color="text.primary" sx={{ fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif' }}>Auto Buybacks Active</Typography>
      </Paper>
    </Box>
  );
};

/* ─────────────────────────────────────────────────────────────
   ECOSYSTEM DIAGRAM  (faithful to the hand-drawn sketch)
   ─────────────────────────────────────────────────────────────*/
/* ─── DCA Strategy Chart (SVG) ──────────────────────────────── */
const DCAChart = ({ isDark }) => {
  const gridC = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(10,16,32,0.1)';

  // 4 equidistant price levels (lower y = higher price in SVG)
  const levels = [16, 52, 88, 124];

  // Upward-trending zigzag: each sell is progressively higher than the last
  const pts = [
    { x: 16,  y: 88,  t: 'n'    },
    { x: 58,  y: 124, t: 'buy'  },
    { x: 100, y: 52,  t: 'sell' },
    { x: 140, y: 88,  t: 'buy'  },
    { x: 182, y: 16,  t: 'sell' },
    { x: 222, y: 52,  t: 'buy'  },
    { x: 268, y: 4,   t: 'sell' },
  ];

  return (
    <svg viewBox="0 0 290 142" width="100%" style={{ display: 'block' }}>
      <defs>
        <marker id="arr-o" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" fill="#F7931A" />
        </marker>
        <marker id="arr-b" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" fill="#2A5ADA" />
        </marker>
      </defs>

      {/* Horizontal dashed price level lines */}
      {levels.map((y, i) => (
        <line key={i} x1="10" y1={y} x2="280" y2={y}
          stroke={gridC} strokeWidth="1" strokeDasharray="5,4" />
      ))}

      {/* Colored directional segments with arrowheads */}
      {pts.slice(1).map((p, i) => {
        const from   = pts[i];
        const isDown = p.y > from.y;
        return (
          <line key={i}
            x1={from.x} y1={from.y} x2={p.x} y2={p.y}
            stroke={isDown ? '#F7931A' : '#2A5ADA'}
            strokeWidth="2"
            markerEnd={`url(#${isDown ? 'arr-o' : 'arr-b'})`}
          />
        );
      })}

      {/* Node circles (buy = orange, sell = blue) */}
      {pts.map((p, i) => {
        if (p.t === 'buy')  return <circle key={i} cx={p.x} cy={p.y} r="5.5" fill="#F7931A" stroke="white" strokeWidth="1.5" />;
        if (p.t === 'sell') return <circle key={i} cx={p.x} cy={p.y} r="5.5" fill="#2A5ADA" stroke="white" strokeWidth="1.5" />;
        return null;
      })}

      {/* Final upward trend arrow */}
      <line x1="268" y1="4" x2="282" y2="4"
        stroke="#2A5ADA" strokeWidth="2" markerEnd="url(#arr-b)" />
    </svg>
  );
};

/* ─── Market Cycle Donut ─────────────────────────────────────── */
const MarketCycleDonut = ({ isDark }) => {
  const centerC = isDark ? 'rgba(255,255,255,0.7)'  : 'rgba(10,16,32,0.7)';
  const subC    = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(10,16,32,0.45)';

  // 4 segments: orange peak → gold transition → teal fear → green recovery
  const cycleData = [
    { value: 90, color: '#F7931A' },
    { value: 90, color: '#e8a84a' },
    { value: 90, color: '#14b8a6' },
    { value: 90, color: '#22c55e' },
  ];

  return (
    <Box sx={{ position: 'relative', width: 260, height: 260, mx: 'auto' }}>
      {/* Recharts donut — centred inside the container */}
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <PieChart width={200} height={200}>
          <Pie data={cycleData} cx={100} cy={100}
            innerRadius={60} outerRadius={85}
            startAngle={90} endAngle={-270}
            dataKey="value" stroke="none" paddingAngle={2}>
            {cycleData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
        </PieChart>
      </Box>

      {/* Center label */}
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, color: centerC, letterSpacing: '0.07em', lineHeight: 1.6, textAlign: 'center' }}>
          DYNAMIC<br />TARGET<br />EXPOSURE
        </Typography>
      </Box>

      {/* EUPHORIA label — top-right with arrow pointing left-down to orange segment */}
      <Box sx={{ position: 'absolute', top: 8, right: 0, textAlign: 'right' }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#F7931A', lineHeight: 1.2 }}>EUPHORIA</Typography>
        <Typography sx={{ fontSize: '0.58rem', color: subC, lineHeight: 1.3 }}>(Lowering Target)</Typography>
        {/* Arrow line */}
        <svg width="32" height="22" style={{ display: 'block', marginLeft: 'auto' }}>
          <defs>
            <marker id="eu-arr" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
              <polygon points="0 0, 5 2.5, 0 5" fill="#F7931A" />
            </marker>
          </defs>
          <line x1="30" y1="2" x2="4" y2="20" stroke="#F7931A" strokeWidth="1.5" markerEnd="url(#eu-arr)" />
        </svg>
      </Box>

      {/* FEAR label — bottom-center with arrow pointing up to teal segment */}
      <Box sx={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
        <svg width="16" height="18" style={{ display: 'block', margin: '0 auto' }}>
          <defs>
            <marker id="fe-arr" markerWidth="5" markerHeight="5" refX="2.5" refY="4" orient="auto">
              <polygon points="0 5, 2.5 0, 5 5" fill="#14b8a6" />
            </marker>
          </defs>
          <line x1="8" y1="16" x2="8" y2="2" stroke="#14b8a6" strokeWidth="1.5" markerEnd="url(#fe-arr)" />
        </svg>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#14b8a6', lineHeight: 1.2 }}>FEAR</Typography>
        <Typography sx={{ fontSize: '0.58rem', color: subC, lineHeight: 1.3 }}>(Increasing Target)</Typography>
      </Box>
    </Box>
  );
};

const EcosystemDiagram = () => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const nodeSx = (variant = 'default') => ({
    p: 2.5,
    borderRadius: 2,
    height: '100%',
    ...(variant === 'v1' && {
      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(10,16,32,0.03)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,16,32,0.09)'}`,
    }),
    ...(variant === 'v2' && {
      bgcolor: isDark ? 'rgba(210,157,92,0.06)' : 'rgba(200,144,74,0.05)',
      border: `1.5px solid ${isDark ? 'rgba(210,157,92,0.3)' : 'rgba(200,144,74,0.3)'}`,
      boxShadow: `0 0 24px ${isDark ? 'rgba(210,157,92,0.08)' : 'rgba(200,144,74,0.08)'}`,
    }),
    ...(variant === 'center' && {
      bgcolor: isDark ? 'rgba(210,157,92,0.12)' : 'rgba(200,144,74,0.08)',
      border: `2px solid ${theme.palette.primary.main}`,
      boxShadow: `0 0 32px ${isDark ? 'rgba(210,157,92,0.15)' : 'rgba(200,144,74,0.12)'}`,
    }),
    ...(variant === 'target' && {
      bgcolor: isDark ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)',
      border: '1.5px solid rgba(34,197,94,0.3)',
    }),
    ...(variant === 'side' && {
      bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(10,16,32,0.02)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(10,16,32,0.08)'}`,
    }),
  });

  /* Arrow cols now delegate to the animated FlowLine component */

  return (
    <Box>
      {/* ── ROW 1: EBONDS 1.0  →YIELD→  EBONDS 2.0 ── */}
      <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Box sx={nodeSx('v1')}>
            <Chip label="EBONDS 1.0" size="small" sx={{ mb: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(10,16,32,0.06)', color: 'text.secondary', fontWeight: 700 }} />
            <Typography variant="h6" color="text.primary" gutterBottom>DeFi Farming</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Structural Constraints</Typography>
            <Stack spacing={0.75}>
              {['Volume-dependent yield', 'High inflation pressure', 'Market-cycle constrained'].map(t => (
                <Stack key={t} direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.disabled">{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Grid>

        <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
            <FlowLine label="YIELD" dir="right" />
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box sx={{ ...nodeSx('v2'), animation: `${breatheGold} 5s ease-in-out infinite 0.8s` }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Chip label="EBONDS 2.0" size="small" sx={{ bgcolor: isDark ? 'rgba(210,157,92,0.12)' : 'rgba(200,144,74,0.1)', color: 'primary.main', fontWeight: 700, border: `1px solid ${isDark ? 'rgba(210,157,92,0.25)' : 'rgba(200,144,74,0.25)'}` }} />
              <Stack direction="row" spacing={0.75}>
                {['Optimized Bots', 'Live Onchain', 'Auto Buybacks'].map(t => (
                  <Chip key={t} label={t} size="small" sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(10,16,32,0.04)', color: 'text.secondary', fontSize: '0.65rem', height: 20 }} />
                ))}
              </Stack>
            </Stack>
            <Typography variant="h6" color="text.primary" gutterBottom>Volatility Harnessing Strategies</Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              {[
                { icon: <SmartToyIcon sx={{ fontSize: 16 }} />, t: 'Optimized Bots',  d: 'Strategies improved by on-chain performance data' },
                { icon: <BoltIcon     sx={{ fontSize: 16 }} />, t: 'Live Onchain',  d: '24/7 execution across multi assets Bots' },
                { icon: <AutorenewIcon sx={{ fontSize: 16 }}/>, t: 'Auto Buybacks', d: 'Revenue → EBONDS $0.90 & ESIR 2–16% APY' },
              ].map(f => (
                <Grid item xs={12} sm={4} key={f.t}>
                  <Box sx={{ p: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(10,16,32,0.025)', borderRadius: 1.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5, color: 'primary.main' }}>
                      {f.icon}
                      <Typography variant="caption" color="text.primary" sx={{ fontWeight: 700 }}>{f.t}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>{f.d}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* ── ROW 2: SEEDING ←→ YOU EBONDS ←→ STAKING ── */}
      <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Box sx={nodeSx('side')}>
            <SavingsIcon sx={{ color: 'primary.main', fontSize: 20, mb: 1 }} />
            <Typography variant="body2" color="text.primary" fontWeight={700} gutterBottom>Seeding + Volume Bonuses</Typography>
            <Stack spacing={0.5}>
              {['EBONDS with discounts', 'More EBONDS to stake', 'Seeding → more buybacks'].map(t => (
                <Stack key={t} direction="row" spacing={0.75} alignItems="flex-start">
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main', mt: '5px', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Grid>

        <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <FlowLine label="USDC"   dir="left"  delay="0s"    />
            <FlowLine label="EBONDS" dir="right" delay="0.9s"  />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ ...nodeSx('center'), textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, animation: `${breatheGold} 4s ease-in-out infinite` }}>
            {/* Rotating dashed ring */}
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1.5 }}>
              <Box sx={{
                position: 'absolute', inset: -10, borderRadius: '50%',
                border: `2px dashed ${theme.palette.primary.main}`,
                opacity: 0.35,
                animation: `${rotateSlow} 14s linear infinite`,
                pointerEvents: 'none',
              }} />
              <Box sx={{
                position: 'absolute', inset: -20, borderRadius: '50%',
                border: `1px dashed ${theme.palette.primary.main}`,
                opacity: 0.15,
                animation: `${rotateSlow} 22s linear infinite reverse`,
                pointerEvents: 'none',
              }} />
              <Avatar sx={{ width: 52, height: 52, bgcolor: isDark ? 'rgba(210,157,92,0.15)' : '#fdf6ee', border: `2px solid ${theme.palette.primary.main}` }}>
                <img src={Logo} alt="" style={{ width: 34, height: 34, objectFit: 'contain' }} />
              </Avatar>
            </Box>
            <Typography variant="h5" color="text.primary" fontWeight={800} sx={{ fontFamily: '"Space Grotesk", sans-serif' }}>YOU</Typography>
            <Typography variant="body2" color="primary.main" fontWeight={700}>EBONDS</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, maxWidth: 160, lineHeight: 1.6 }}>
              Your allocation at the center of the flywheel
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <FlowLine label="EBONDS" dir="right" delay="0.4s"  />
            <FlowLine label="ESIR"   dir="left"  delay="1.3s"  />
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box sx={nodeSx('side')}>
            <ShowChartIcon sx={{ color: 'primary.main', fontSize: 20, mb: 1 }} />
            <Typography variant="body2" color="text.primary" fontWeight={700} gutterBottom>Staking EBONDS</Typography>
            <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ display: 'block', mb: 1 }}>Earn ESIR</Typography>
            <Stack spacing={0.5}>
              {['Stake EBONDS, receive ESIR', '2–16% APY from bot revenue', 'Uncapped staking window'].map(t => (
                <Stack key={t} direction="row" spacing={0.75} alignItems="flex-start">
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main', mt: '5px', flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>

      {/* ── CENTER ARROWS DOWN ── */}
      <Grid container sx={{ mb: 2 }}>
        <Grid item xs={12} md={4} />
        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
          <FlowLine label="EBONDS / ESIR" dir="down" delay="0s"   />
          <FlowLine label="USDC"          dir="up"   delay="1.0s" />
        </Grid>
        <Grid item xs={12} md={4} />
      </Grid>

      {/* ── ROW 3: BUYBACK TARGETS ── */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} />
        <Grid item xs={12} md={4}>
          <Box sx={{ ...nodeSx('target'), animation: `${breatheGreen} 3.5s ease-in-out infinite` }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <AutorenewIcon sx={{ color: '#22c55e', fontSize: 18, animation: `${rotateSlow} 4s linear infinite` }} />
              <Typography variant="body2" color="text.primary" fontWeight={700}>Buyback Targets</Typography>
            </Stack>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: isDark ? 'rgba(34,197,94,0.08)' : '#f0fdf4', borderRadius: 1.5, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: '#22c55e', fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800 }}>$0.90</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>EBONDS floor</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: isDark ? 'rgba(34,197,94,0.08)' : '#f0fdf4', borderRadius: 1.5, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: '#22c55e', fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800 }}>2–16%</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>ESIR APY</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} md={4} />
      </Grid>

      {/* ── BUYBACKS from EBONDS 2.0 label ── */}
      <Box sx={{ mt: 1.5, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          ↗ EBONDS 2.0 bot revenue flows into Buyback Targets via programmatic USDC routing
        </Typography>
      </Box>
    </Box>
  );
};

/* ─────────────────────────────────────────────────────────────
   PAGE
   ─────────────────────────────────────────────────────────────*/
const LandingPage = () => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isDark    = theme.palette.mode === 'dark';

  const accent       = theme.palette.primary.main;
  const sectionLight = isDark ? 'rgba(255,255,255,0.01)' : '#f7f9fc';

  return (
    <Box sx={{ bgcolor: 'background.default', pb: 0 }}>

      {/* ══════════════════════════════════════════
          1 · HERO
      ══════════════════════════════════════════ */}
      <Box sx={{
        bgcolor: 'background.paper',
        pt: { xs: 16, md: 20 }, pb: { xs: 10, md: 14 },
        position: 'relative', overflow: 'hidden',
        ...(isDark && {
          backgroundImage: `radial-gradient(ellipse at 10% 40%, rgba(210,157,92,0.1) 0%, transparent 55%),
                            radial-gradient(ellipse at 90% 20%, rgba(210,157,92,0.05) 0%, transparent 45%)`,
        }),
      }}>
        {isDark && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 0,
            backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
            backgroundSize: '44px 44px',
            maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
          }} />
        )}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, md: 12 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2.5 }}>
                Live on Arbitrum
              </Typography>
              <Typography variant="h1" color="text.primary" sx={{ mb: 3, fontSize: { xs: '2.8rem', md: '4rem' } }}>
                Automated Strategies.<br />
                Real buybacks.<br />
                <Box component="span" sx={{ color: accent }}>Sustainable rewards.</Box>
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 480, lineHeight: 1.9, fontSize: '1.05rem' }}>
                EBONDS 2.0 deploys automated on-chain bots, capturing volatility across wBTC, ETH & LINK. Every sell event triggers an automated buyback. Earn passive income staking EBONDS, targeting a sustainable APY, priotizing scalability and protocol growth.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 6 }}>
                <Button
                  variant="contained" size="large" endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/sales')}
                  sx={{ px: 3.5, py: 1.7, fontSize: '0.95rem', boxShadow: `0 4px 20px ${isDark ? 'rgba(210,157,92,0.35)' : 'rgba(200,144,74,0.3)'} !important` }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined" size="large"
                  onClick={() => navigate('/allocation-staking')}
                  sx={{ px: 3.5, py: 1.7, fontSize: '0.95rem' }}
                >
                  Open Dashboard
                </Button>
              </Stack>

              <Stack direction="row" spacing={3} alignItems="center">
                <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.7rem' }}>ONCHAIN ONLY</Typography>
                {['LIVE AUTONOMOUS BUYBACKS'].map(t => (
                  <Typography key={t} variant="body2" color="text.disabled" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.75rem' }}>{t}</Typography>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
              <LiveSystemCard />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════
          2 · FEATURE ROW
      ══════════════════════════════════════════ */}
      <Box sx={{ bgcolor: sectionLight, py: { xs: 10, md: 14 }, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} sx={{ mb: 8 }}>
            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>VOLATILITY STRATEGIES</Typography>
              <Typography variant="h2" color="text.primary" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
                Revenue generated from<br />market activity.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9 }}>
                The strategies operate on market movements, volatility, not trading volume. Bots work onchain 24/7 and every sell event from bots trigger an automated onchain buyback.
              </Typography>
            </Grid>
          </Grid>

          <Stack spacing={3}>

            {/* ── Card 1: Active DCA Strategies ── */}
            <Paper sx={{ p: { xs: 3, md: 4 }, overflow: 'hidden' }}>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={7}>

                  {/* Icon cluster */}
                  <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ mb: 2.5 }}>
                    <Box sx={{ position: 'relative' }}>
                      <Box sx={{ width: 62, height: 62, borderRadius: '50%', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f3f4f6', border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SmartToyIcon sx={{ fontSize: 34, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)' }} />
                      </Box>
                      {/* <Box sx={{ position: 'absolute', bottom: -2, right: -6, width: 26, height: 26, borderRadius: '50%', bgcolor: '#F7931A', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid', borderColor: 'background.paper' }}>
                        <SiBitcoin style={{ color: '#fff', fontSize: 14 }} />
                      </Box> */}
                    </Box>
                    {/* <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#2A5ADA', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '2px' }}>
                      <SiChainlink style={{ color: '#fff', fontSize: 18 }} />
                    </Box> */}
                  </Stack>

                  <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700, mb: 2 }}>
                    Active DCA Strategies {' '}
              
        
                    {' — Uniswap V3'}
                  </Typography>

                  <Stack spacing={1.25}>
                    {[
                      'Buying down at equidistant price levels',
                      'Selling up to capitalize on the volatility',
                      'Realizing a profit on each move upwards after a previous buy event',
                    ].map(text => (
                      <Stack key={text} direction="row" spacing={1.25} alignItems="flex-start">
                        <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: 'rgba(34,197,94,0.12)', border: '1.5px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '1px', flexShrink: 0 }}>
                          <Typography sx={{ color: '#22c55e', fontSize: '0.65rem', lineHeight: 1, fontWeight: 700 }}>✓</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{text}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={5}>
                  <DCAChart isDark={isDark} />
                </Grid>
              </Grid>
            </Paper>

            {/* ── Card 2: Maximum Exposure Strategy ── */}
            <Paper sx={{ p: { xs: 3, md: 4 }, overflow: 'hidden' }}>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={7}>

                  {/* Icon cluster */}
                  <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ mb: 2.5 }}>
                    <Box sx={{ position: 'relative' }}>
                      <Box sx={{ width: 62, height: 62, borderRadius: '50%', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f3f4f6', border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SmartToyIcon sx={{ fontSize: 34, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)' }} />
                      </Box>
                      
                    </Box>
                  </Stack>

                  <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700, mb: 2 }}>
                    Maximum Exposure Strategy {' '}
                  
                    {' — Uniswap V3'}
                  </Typography>

                  <Stack spacing={1.25}>
                    {[
                      'When above target exposure, rebalances selling surplus exposure',
                      'When below exposure, rebalances buying back to target exposure',
                      'Each sell event generates a profit',
                      'Target Exposure changes dynamically according to market cycles',
                      'Increasing on deeper corrections (fear) and lowering on new multiples (euphoria)',
                    ].map(text => (
                      <Stack key={text} direction="row" spacing={1.25} alignItems="flex-start">
                        <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: 'rgba(34,197,94,0.12)', border: '1.5px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '1px', flexShrink: 0 }}>
                          <Typography sx={{ color: '#22c55e', fontSize: '0.65rem', lineHeight: 1, fontWeight: 700 }}>✓</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{text}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MarketCycleDonut isDark={isDark} />
                </Grid>
              </Grid>
            </Paper>

          </Stack>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════
          3 · STATS CARDS
      ══════════════════════════════════════════ */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 10, md: 14 }, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>why allocators choose ebonds</Typography>
          <Typography variant="h2" color="text.primary" sx={{ mb: 8, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>Built around the buyback mechanism.</Typography>
          <Grid container spacing={3}>
            {/* APY card — shimmer headline */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, height: '100%', overflow: 'hidden', position: 'relative' }}>
                <Typography variant="h1" sx={{
                  fontSize: '3.8rem', lineHeight: 1, mb: 1,
                  background: isDark
                    ? `linear-gradient(90deg, #d29d5c 0%, #fff8ee 40%, #d29d5c 60%, #e3b578 100%)`
                    : `linear-gradient(90deg, #c8904a 0%, #6b3d10 40%, #c8904a 60%, #dba96a 100%)`,
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: `${shimmerText} 3.5s linear infinite`,
                }}>
                  6–34%
                </Typography>
                <Typography variant="h6" color="text.primary" gutterBottom>Bots Layer Combined APY</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>Driven by volatility — no volume dependency.</Typography>
                {/* Subtle corner glow */}
                <Box sx={{ position: 'absolute', bottom: -20, right: -20, width: 80, height: 80, bgcolor: accent, filter: 'blur(40px)', opacity: 0.12, borderRadius: '50%', pointerEvents: 'none' }} />
              </Paper>
            </Grid>

            {/* Buybacks card — pulsing icon pair */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2.5, bgcolor: '#ecfdf5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: `${breatheGreen} 3s ease-in-out infinite`,
                  }}>
                    <AutorenewIcon sx={{ color: '#059669', fontSize: 22, animation: `${rotateSlow} 5s linear infinite` }} />
                  </Box>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2.5,
                    bgcolor: isDark ? 'rgba(210,157,92,0.1)' : '#fdf6ee',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: `${breatheGold} 3s ease-in-out infinite 1.5s`,
                  }}>
                    <ShowChartIcon sx={{ color: accent, fontSize: 22 }} />
                  </Box>
                </Stack>
                <Typography variant="h6" color="text.primary" gutterBottom>Automated Buybacks</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>No cron jobs. No manual triggers. Bots buy EBONDS and ESIR from the realized profits directly on-chain whenever a sell event occurs.</Typography>
              </Paper>
            </Grid>

            {/* ESIR card — bars animate in on mount */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.72rem' }}>TARGET SUSTAINABLE APY</Typography>
                <Typography variant="h2" color="text.primary" sx={{ fontSize: '2.8rem', mb: 0.5 }}>2–16%</Typography>
                <Typography variant="caption" sx={{ color: accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 2.5 }}>Dynamic Range</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>Yield adjusts dynamically based on protocol outflows, buyback volume and performance.</Typography>

              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════
          4 · STEPS
      ══════════════════════════════════════════ */}
      <Box sx={{
        bgcolor: isDark ? '#0d1a2a' : '#0e1c2e',
        py: { xs: 10, md: 16 },
        borderTop: `1px solid ${theme.palette.divider}`,
        position: 'relative', overflow: 'hidden',
        backgroundImage: `
          radial-gradient(ellipse at 15% 60%, rgba(200,144,74,0.07) 0%, transparent 50%),
          radial-gradient(ellipse at 85% 30%, rgba(42,90,218,0.05) 0%, transparent 50%)
        `,
      }}>
        <Container maxWidth="lg">

          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.28)', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', mb: 2, display: 'block' }}>
              GET STARTED
            </Typography>
            <Typography variant="h2" sx={{ color: '#ffffff', fontSize: { xs: '2rem', md: '2.8rem' }, lineHeight: 1.2 }}>
              Three simple steps to activate yield
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.32)', mt: 2, maxWidth: 460, mx: 'auto', lineHeight: 1.85 }}>
              Start earning sustainable rewards in just a few steps.
            </Typography>
          </Box>

          <Box sx={{ position: 'relative' }}>
            <Box sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute', top: 26,
              left: 'calc(16.67% + 26px)', right: 'calc(16.67% + 26px)',
              height: '1px',
              background: 'linear-gradient(90deg, rgba(210,157,92,0.6) 0%, rgba(210,157,92,0.18) 50%, rgba(210,157,92,0.6) 100%)',
              zIndex: 0,
            }} />

            <Grid container spacing={3}>
              {[
                {
                  n: '01',
                  icon: <SavingsIcon sx={{ fontSize: 24 }} />,
                  title: 'Send your allocation',
                  body: 'Acquire EBONDS as low as $0.66 with the 30% Volume Bonues. Limited to Soft targets.',
                },
                {
                  n: '02',
                  icon: <ShowChartIcon sx={{ fontSize: 24 }} />,
                  title: 'Stake and earn ESIR',
                  body: 'Stake EBONDS and begin receiving daily ESIR rewards.',
                },
                {
                  n: '03',
                  icon: <AutorenewIcon sx={{ fontSize: 24 }} />,
                  title: 'Let the flywheel work',
                  body: 'Volatility leads to Autobuybacks and liquidity growth, delta growth compounds the system.',
                },
              ].map((s) => (
                <Grid item xs={12} md={4} key={s.n}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', position: 'relative', zIndex: 1 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: '50%', flexShrink: 0, mb: 4,
                      bgcolor: 'rgba(210,157,92,0.1)', border: '1.5px solid rgba(210,157,92,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 0 22px rgba(210,157,92,0.18)',
                    }}>
                      <Typography sx={{ color: '#d29d5c', fontWeight: 800, fontSize: '0.9rem', fontFamily: '"Space Grotesk", sans-serif', letterSpacing: '0.04em' }}>
                        {s.n}
                      </Typography>
                    </Box>
                    <Box sx={{
                      p: { xs: 3, md: 3.5 }, width: '100%', borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.055)', borderColor: 'rgba(210,157,92,0.22)',
                        transform: 'translateY(-5px)', boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
                      },
                    }}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: 2.5, mx: 'auto', mb: 2.5,
                        bgcolor: 'rgba(210,157,92,0.08)', border: '1px solid rgba(210,157,92,0.14)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d29d5c',
                      }}>
                        {s.icon}
                      </Box>
                      <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1.5 }}>{s.title}</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', lineHeight: 1.85 }}>{s.body}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

        </Container>
      </Box>

      {/* ══════════════════════════════════════════
          5 · ECOSYSTEM DIAGRAM  (sketch faithfully)
      ══════════════════════════════════════════ */}
      <Box sx={{ bgcolor: sectionLight, py: { xs: 10, md: 16 }, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 8 }}>
            <Typography variant="body2" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>THE ONCHAIN FLYWHEEL</Typography>
            <Typography variant="h2" color="text.primary" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, maxWidth: 600 }}>
              The onchain flywheel<br />
              <Box component="span" sx={{ color: accent }}>powering sustainable revenue</Box>
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 560, lineHeight: 1.85 }}>
              DeFi Farming and Volatility Bots complement each other and our Auto Buyback Engine triggers buybacks with every move upwards hitting our take profit threshold target. The more liquidity you seed, the more the buyback engine has to work with - aligning incentives between holders and the protocol.
            </Typography>
          </Box>
          <EcosystemDiagram />
        </Container>
      </Box>

      {/* ══════════════════════════════════════════
          6 · MISSION NUMBERS
      ══════════════════════════════════════════ */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 10, md: 14 }, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="body2" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2, display: 'block' }}>OUR TARGETS</Typography>
            <Typography variant="h2" color="text.primary" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
              Built to generate sustainable returns
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {[
              { value: '6–34%', label: 'Bots Layer Combined APY ',    sub: 'BTC, ETH & LINK STRATEGIES' },
              { value: '$0.90',  label: 'EBONDS buyback floor', sub: 'Auto buyback target' },
              { value: '2–16%',  label: 'ESIR APY range',       sub: 'Staking reward' },
              { value: '30%',    label: 'Max seeding bonus',    sub: 'Allocation bonus tier' },
            ].map((s, i) => (
              <Grid item xs={6} md={3} key={i} sx={{ textAlign: 'center' }}>
                <Typography variant="h2" color="text.primary" sx={{ fontSize: { xs: '2.2rem', md: '2.8rem' }, mb: 0.5 }}>{s.value}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{s.label}</Typography>
                <Typography variant="caption" sx={{ color: accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.sub}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════
          7 · SMARTER INVEST
      ══════════════════════════════════════════ */}
      <Box sx={{ bgcolor: sectionLight, py: { xs: 10, md: 14 }, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="lg">

          {/* Header */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="body2" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
              SCALE YOUR POSITION
            </Typography>
            <Typography variant="h2" color="text.primary" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 2 }}>
              The more you invest,<br />
              <Box component="span" sx={{ color: accent }}>the more you earn.</Box>
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9, maxWidth: 600 }}>
              EBONDS' incentive structure rewards commitment. Larger allocations unlock a deeper discount on the EBONDS purchase price — turning your bonus into immediate embedded value.
            </Typography>
          </Box>

          {/* Two cards */}
          <Grid container spacing={3}>

            {/* LEFT card — Seeding Bonus */}
            <Grid item xs={12} md={6}>
              <Paper sx={{
                p: 4, height: '100%',
                bgcolor: isDark ? 'rgba(210,157,92,0.06)' : 'rgba(200,144,74,0.04)',
                border: `1.5px solid ${isDark ? 'rgba(210,157,92,0.25)' : 'rgba(200,144,74,0.25)'}`,
              }}>
                <Typography variant="body2" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
                  SEEDING BONUS
                </Typography>
                <Typography sx={{
                  fontSize: '3.5rem', fontWeight: 800, lineHeight: 1, mb: 2,
                  fontFamily: '"Space Grotesk", sans-serif', color: accent,
                }}>
                  Up to +30%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
                  Every additional dollar you allocate reduces your per-EBONDS purchase price. At $150K you buy EBONDS at $0.6654 — already 35.3% below the $0.90 target price before any APY is counted.
                </Typography>
                <Button
                  variant="outlined" fullWidth endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/sales')}
                  sx={{ py: 1.4 }}
                >
                  Get Bonus Allocation
                </Button>
              </Paper>
            </Grid>

            {/* RIGHT card — Why it compounds */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography variant="h6" color="text.primary" fontWeight={700} sx={{ mb: 3 }}>
                  Why the bonus compounds
                </Typography>
                <Stack spacing={2}>
                  {[
                    'Lower entry price = more EBONDS per dollar',
                    'More EBONDS = more ESIR earned daily',
                    'Higher upside when buybacks push price to $0.90',
                  ].map(t => (
                    <Stack key={t} direction="row" spacing={1.5} alignItems="flex-start">
                      <Typography color="primary.main" fontWeight={700} sx={{ flexShrink: 0, mt: '1px' }}>→</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{t}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════
          8 · FINAL CTA
      ══════════════════════════════════════════ */}
      <Box sx={{
        bgcolor: isDark ? '#0d1a2a' : '#0e1c2e',
        py: { xs: 10, md: 14 },
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundImage: 'radial-gradient(ellipse at 20% 60%, rgba(200,144,74,0.08) 0%, transparent 50%)',
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.25)', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 2, display: 'block' }}>
              ACTIVATE AUTOMATED REVENUE
            </Typography>
            <Typography variant="h2" sx={{ color: '#ffffff', mb: 2.5, fontSize: { xs: '2rem', md: '2.6rem' }, lineHeight: 1.25 }}>
              Ready to activate<br />automated DeFi revenue?
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.38)', lineHeight: 1.85, maxWidth: 520, mx: 'auto' }}>
              Support onchain liquidity infrastructure and earn sustainable rewards through automated buybacks, staking emissions, and protocol growth.
            </Typography>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default LandingPage;
